import React, { useState, useEffect } from 'react';
import { Shield, Clock, Zap, HeadphonesIcon, Network, Server, Check } from 'lucide-react';
import { contactInfo } from '../mockData';
import { plansAPI } from '../services/api';

const ForBusiness = () => {
  const [businessPlans, setBusinessPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBusinessPlans();
  }, []);

  const fetchBusinessPlans = async () => {
    try {
      const response = await plansAPI.getBusiness();
      setBusinessPlans(response.data);
    } catch (error) {
      console.error('Error fetching business plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const businessBenefits = [
    {
      icon: Shield,
      title: 'SLA Garantido',
      description: 'Garantia de disponibilidade e suporte prioritário'
    },
    {
      icon: Clock,
      title: 'Suporte 24/7',
      description: 'Atendimento especializado a qualquer hora'
    },
    {
      icon: Zap,
      title: 'Velocidade Simétrica',
      description: 'Mesma velocidade de download e upload'
    },
    {
      icon: Network,
      title: 'IP Fixo',
      description: 'Endereço IP dedicado para sua empresa'
    },
    {
      icon: Server,
      title: 'Armazenamento Local',
      description: 'Seus dados seguros em servidores locais, sem dependência de nuvem'
    },
    {
      icon: HeadphonesIcon,
      title: 'Gerente de Conta',
      description: 'Profissional dedicado para atender sua empresa'
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-orange-500 to-orange-600 text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-6">Internet Para Sua Empresa</h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto">
            Soluções corporativas com a performance e confiabilidade que seu negócio precisa
          </p>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-800 mb-12">
            Diferenciais para empresas
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {businessBenefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div
                  key={index}
                  className="bg-gray-50 p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all hover:-translate-y-2"
                >
                  <div className="bg-orange-500 w-16 h-16 rounded-full flex items-center justify-center mb-4">
                    <Icon className="text-white" size={32} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Business Plans Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-800 mb-12">
            Planos Empresariais
          </h2>
          {loading ? (
            <p className="text-center text-xl text-gray-600">Carregando planos...</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {businessPlans.map((plan) => (
                <div
                  key={plan.id}
                  className="bg-white rounded-2xl shadow-lg overflow-hidden border-2 border-orange-500"
                >
                  <div className="bg-gradient-to-br from-orange-500 to-orange-600 text-white p-6 text-center">
                    <div className="text-6xl font-bold mb-2">{plan.speed}</div>
                    <div className="text-2xl font-bold">Empresarial</div>
                  </div>

                  <div className="p-6">
                    <div className="text-center mb-6">
                      <div className="flex items-start justify-center">
                        <span className="text-2xl font-bold text-gray-800">R$</span>
                        <span className="text-5xl font-bold text-gray-800 mx-1">
                          {plan.price.split(',')[0]}
                        </span>
                        <div className="flex flex-col items-start">
                          <span className="text-2xl font-bold text-gray-800">,{plan.price.split(',')[1]}</span>
                          <span className="text-sm text-gray-600">/mês</span>
                        </div>
                      </div>
                    </div>

                    <ul className="space-y-3 mb-6">
                      {plan.features.map((feature, index) => (
                        <li key={index} className="flex items-start">
                          <Check className="text-orange-500 mr-2 flex-shrink-0 mt-1" size={20} />
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <a
                      href={contactInfo.whatsappLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full bg-orange-500 text-white py-3 rounded-lg text-center font-bold hover:bg-orange-600 transition-colors"
                    >
                      Solicitar Orçamento
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Products and Solutions Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-800 mb-12">
            Conheça nossas soluções e produtos
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Rede Interna */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all">
              <div className="h-48 overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1606857521015-7f9fcf423740?crop=entropy&cs=srgb&fm=jpg&q=85" 
                  alt="Rede Interna"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-800 mb-3">Rede interna</h3>
                <p className="text-gray-600">
                  Estruturação de rede de dados cabeada ou Wireless com equipamentos de última geração para garantir estabilidade e velocidade em sua empresa.
                </p>
              </div>
            </div>

            {/* Wi-Fi de Alta Performance */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all">
              <div className="h-48 overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1743865318581-2e0e59e7292e?crop=entropy&cs=srgb&fm=jpg&q=85" 
                  alt="Wi-Fi de Alta Performance"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-800 mb-3">Wi-Fi de alta performance</h3>
                <p className="text-gray-600">
                  Ampliação do alcance e potência do sinal de Wi-Fi, através de equipamentos de última geração, que proporcionam melhor desempenho da rede Wireless.
                </p>
              </div>
            </div>

            {/* IP Público Exclusivo */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all">
              <div className="h-48 overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1580894908361-967195033215?crop=entropy&cs=srgb&fm=jpg&q=85" 
                  alt="IP Público Exclusivo"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-800 mb-3">IP público exclusivo</h3>
                <p className="text-gray-600">
                  Tenha um endereço IP exclusivo para sua empresa, ideal para hospedar servidores, sistemas de segurança e acesso remoto com total controle.
                </p>
              </div>
            </div>

            {/* Rede Adicional */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all">
              <div className="h-48 overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1709715357520-5e1047a2b691?crop=entropy&cs=srgb&fm=jpg&q=85" 
                  alt="Rede Adicional"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-800 mb-3">Rede adicional</h3>
                <p className="text-gray-600">
                  Instalação de pontos de rede adicionais para expandir sua infraestrutura, permitindo mais dispositivos conectados com qualidade e estabilidade.
                </p>
              </div>
            </div>

            {/* Link Dedicado */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all">
              <div className="h-48 overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1573164574572-cb89e39749b4?crop=entropy&cs=srgb&fm=jpg&q=85" 
                  alt="Link Dedicado"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-800 mb-3">Link dedicado</h3>
                <p className="text-gray-600">
                  Conexão exclusiva com banda garantida e simétrica, perfeita para empresas que precisam de alta disponibilidade e performance constante.
                </p>
              </div>
            </div>

            {/* CFTV */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all">
              <div className="h-48 overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_web-clone-project-10/artifacts/xflpabvy_17262379778614.webp" 
                  alt="CFTV"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-800 mb-3">CFTV - Câmeras de segurança</h3>
                <p className="text-gray-600">
                  Sistema completo de monitoramento com câmeras de alta resolução, armazenamento local e acesso remoto para total segurança do seu negócio.
                </p>
              </div>
            </div>

            {/* Suporte Técnico */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all">
              <div className="h-48 overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1598970434795-0c54fe7c0648?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxoZWxwJTIwZGVza3xlbnwwfHx8fDE3NjQ0MTYxODZ8MA&ixlib=rb-4.1.0&q=85" 
                  alt="Suporte Técnico"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-800 mb-3">Suporte técnico especializado</h3>
                <p className="text-gray-600">
                  Oferecemos um apoio contínuo por profissionais de alta capacidade, garantindo que sua rede funcione perfeitamente 24 horas por dia.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Image Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">Leve sua empresa ao próximo nível</h2>
              <p className="text-lg mb-6">
                Com a OpaLink, sua empresa conta com uma conexão estável, rápida e confiável para
                impulsionar seus negócios.
              </p>
              <a
                href={contactInfo.whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-orange-500 text-white px-8 py-4 rounded-lg text-lg font-bold hover:bg-orange-600 transition-all hover:scale-105 shadow-lg"
              >
                Fale com um consultor
              </a>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1497366216548-37526070297c?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Empresas"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ForBusiness;
